import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAppContext } from "@/contexts";
import { areSexAndGenderPrePopulated } from "@/utils/survey/retriage";
import { shouldShowDefaultSexAndGenderQuestions } from "@/utils/survey/sexAndGender";

import {
  QuestionComponentProps,
  QuestionDefinition,
  QuestionOption,
} from "../../Question";
import { MultipleChoiceQuestion } from "../core";

const QUESTION_ID = "sex";
export const SEX_QUESTION_ID = QUESTION_ID;

declare module "@/contexts" {
  interface SurveyAnswers {
    sex?: string;
  }
}

const SexQuestion: React.FC<QuestionComponentProps> = ({
  onClickBack,
  onClickNext,
  surveyAnswers,
}) => {
  const { partnerInfo, eligibilityInfo, retriageEligibility, selectedProgram } =
    useAppContext();
  const { t } = useTranslation();

  const showDefaultQuestions = useMemo(
    () =>
      shouldShowDefaultSexAndGenderQuestions(
        partnerInfo?.id,
        eligibilityInfo
          ? eligibilityInfo.segmentId
          : retriageEligibility?.segmentId,
      ),
    [partnerInfo, eligibilityInfo, retriageEligibility],
  );

  const allOptions: QuestionOption[] = [
    { id: "male", text: t("sex.options.male") },
    { id: "female", text: t("sex.options.female") },
    { id: "intersex", text: t("sex.options.intersex") },
    { id: "preferNotToSay", text: t("sex.options.preferNotToSay") },
  ];

  const options: QuestionOption[] = showDefaultQuestions
    ? allOptions
    : allOptions.filter((option) => ["male", "female"].includes(option.id));

  const questionDescription =
    selectedProgram === "MOOD" ? "" : t("sex.questionDescription");

  return (
    <MultipleChoiceQuestion
      onClickBack={onClickBack}
      onClickNext={onClickNext}
      options={options}
      questionId={QUESTION_ID}
      questionDescription={questionDescription}
      questionText={t("sex.questionText")}
      selectedItem={surveyAnswers[QUESTION_ID] || ""}
    />
  );
};

export const Q_SEX: QuestionDefinition = {
  id: QUESTION_ID,
  supportedPrograms: [
    "WEIGHT",
    "MOOD",
    "DPP",
    "DIABETES_LIFESTYLE",
    "MED",
    "NMPBA",
  ],
  shouldShowQuestion: () => true,
  component: SexQuestion,
};

export const Q_RETRIAGE_SEX: QuestionDefinition = {
  id: QUESTION_ID,
  supportedPrograms: ["DPP", "DIABETES_LIFESTYLE", "MED", "NMPBA"],
  shouldShowQuestion: ({ retriageEligibility, surveyAnswers }) =>
    !areSexAndGenderPrePopulated(surveyAnswers, retriageEligibility),
  component: SexQuestion,
};
